import React, { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MapContainer, TileLayer, Polyline, Circle, CircleMarker, Tooltip, Marker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { todayISO, fmtDate, fmtDateTime, fmtTime } from "@/lib/utils-app";
import { Users, MapPin, RotateCw, Activity, CalendarDays, Filter } from "lucide-react";
import { StatTile, StatStrip } from "@/components/StatTile";

const COLORS = [
  "#2563eb", "#dc2626", "#059669", "#9333ea", "#ea580c",
  "#0891b2", "#be123c", "#65a30d", "#7c3aed", "#0d9488",
];

// Default Leaflet markers don't load via Webpack/Vite without manual config.
// Replace the default with a simple coloured circle for the head of trail.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-shadow.png",
});

export default function GpsTrailPage() {
  const [date, setDate] = useState(todayISO());
  const [roleFilter, setRoleFilter] = useState("all");
  const [supId, setSupId] = useState("all");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState({ trails: [], parks: [] });
  const [supervisors, setSupervisors] = useState([]);
  const [trackingInterval, setTrackingInterval] = useState(5);

  useEffect(() => {
    api.get("/settings").then((res) => {
      if (res.data?.supervisor_gps_tracking_interval_minutes) {
        setTrackingInterval(res.data.supervisor_gps_tracking_interval_minutes);
      }
    }).catch(() => {});
  }, []);

  const reload = async () => {
    setBusy(true);
    try {
      const params = { date };
      if (roleFilter !== "all") params.role = roleFilter;
      if (supId !== "all") params.supervisor_id = supId;
      const [trail, allSups, sups] = await Promise.all([
        api.get("/admin/gps/trail", { params }),
        api.get("/admin/gps/all-supervisors", { params: roleFilter !== "all" ? { role: roleFilter } : {} }),
        api.get("/admin/gps/supervisors-with-trail", { params: { date } }),
      ]);
      setData(trail.data);
      // Merge: prefer the day's pinged supervisors first (their counts and times),
      // then append any other assigned supervisors so the admin can still pick.
      const dayRows = sups.data?.rows || [];
      const dayIds = new Set(dayRows.map((r) => r.supervisor_id));
      const others = (allSups.data || []).filter((r) => !dayIds.has(r.supervisor_id));
      setSupervisors([...dayRows, ...others]);
    } catch {
      // ignore
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line
  }, [date, roleFilter, supId]);

  const trails = data.trails || [];
  const parks = data.parks || [];

  // Compute map center: average of first ping, or default to Tamil Nadu centre
  const center = useMemo(() => {
    const pts = [];
    for (const t of trails) for (const p of t.pings) pts.push([p.lat, p.long]);
    for (const pk of parks) {
      if (pk.latitude && pk.longitude) pts.push([pk.latitude, pk.longitude]);
    }
    if (pts.length === 0) return [11.1271, 78.6569]; // TN centre
    const lat = pts.reduce((s, p) => s + p[0], 0) / pts.length;
    const lng = pts.reduce((s, p) => s + p[1], 0) / pts.length;
    return [lat, lng];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <AppShell title="Supervisor GPS Trail" subtitle={`Live ${trackingInterval}-minute movement pings overlaid on park geofences`}>
      <div className="flex flex-col gap-4">
        {/* Filter bar */}
        <Card>
          <CardContent className="grid gap-3 p-4 md:grid-cols-12">
            <div className="md:col-span-3 flex flex-col gap-1.5">
              <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                <CalendarDays className="mr-1 inline h-3 w-3" /> Date
              </Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="gps-date-input" className="h-10" />
            </div>
            <div className="md:col-span-3 flex flex-col gap-1.5">
              <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                <Filter className="mr-1 inline h-3 w-3" /> Role
              </Label>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger data-testid="gps-role-select" className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All roles</SelectItem>
                  <SelectItem value="pmc_supervisor">PMC supervisors</SelectItem>
                  <SelectItem value="contractor_supervisor">Contractor supervisors</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-4 flex flex-col gap-1.5">
              <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                <Users className="mr-1 inline h-3 w-3" /> Supervisor
              </Label>
              <Select value={supId} onValueChange={setSupId}>
                <SelectTrigger data-testid="gps-supervisor-select" className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-[60vh]">
                  <SelectItem value="all">All supervisors</SelectItem>
                  {supervisors.map((s) => (
                    <SelectItem key={s.supervisor_id} value={s.supervisor_id}>
                      {s.supervisor_name} ({s.role === "pmc_supervisor" ? "PMC" : "Contractor"})
                      {s.count ? ` · ${s.count} ping${s.count === 1 ? "" : "s"}` : " · no pings"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 flex flex-col gap-1.5">
              <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground opacity-0">
                refresh
              </Label>
              <Button variant="outline" size="sm" onClick={reload} disabled={busy} data-testid="gps-refresh" className="h-10">
                <RotateCw className={`mr-1.5 h-4 w-4 ${busy ? "animate-spin" : ""}`} /> Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stats strip */}
        <StatStrip cols={4}>
          <StatTile icon={Users} accent="indigo" label="Supervisors" value={trails.length} hint={fmtDate(date)} testid="gps-stat-supervisors" />
          <StatTile icon={Activity} accent="emerald" label="Pings" value={trails.reduce((s, t) => s + (t.count || 0), 0)} hint={`${trackingInterval}-min intervals`} testid="gps-stat-pings" />
          <StatTile icon={MapPin} accent="teal" label="Parks" value={parks.length} hint="With geofence" testid="gps-stat-parks" />
          <StatTile
            icon={CalendarDays}
            accent="slate"
            label="First → Last"
            value={
              (() => {
                let first = null, last = null;
                for (const t of trails) for (const p of t.pings) {
                  const dt = new Date(p.captured_at);
                  if (!first || dt < first) first = dt;
                  if (!last || dt > last) last = dt;
                }
                return first && last ? `${fmtTime(first)}–${fmtTime(last)}` : "—";
              })()
            }
            testid="gps-stat-range"
          />
        </StatStrip>

        {/* Map */}
        <Card>
          <CardContent className="p-0">
            <div className="h-[60vh] w-full overflow-hidden rounded-lg" data-testid="gps-map">
              <MapContainer center={center} zoom={trails.length || parks.length ? 12 : 7} className="h-full w-full">
                <TileLayer
                  attribution='&copy; OpenStreetMap'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {parks.map((p) =>
                  p.latitude && p.longitude ? (
                    <Circle
                      key={p.id}
                      center={[p.latitude, p.longitude]}
                      radius={p.geofence_radius_m || 500}
                      pathOptions={{ color: "#475569", weight: 1, fillOpacity: 0.08 }}
                    >
                      <Tooltip>
                        <div className="text-xs">
                          <div className="font-medium">{p.name}</div>
                          <div className="text-muted-foreground">
                            {p.code} · {p.geofence_radius_m || 500}m geofence
                          </div>
                        </div>
                      </Tooltip>
                    </Circle>
                  ) : null,
                )}
                {trails.map((t, i) => {
                  const color = COLORS[i % COLORS.length];
                  const positions = (t.pings || []).map((p) => [p.lat, p.long]);
                  const last = t.pings[t.pings.length - 1];
                  // Show permanent time labels on every Nth ping so the admin
                  // can see times directly on the map. When the trail is short
                  // every ping gets a label; for longer trails we throttle.
                  const total = t.pings.length;
                  const targetLabels = 8;
                  const step = total > targetLabels ? Math.max(1, Math.floor(total / targetLabels)) : 1;
                  return (
                    <React.Fragment key={t.supervisor_id}>
                      {positions.length >= 2 && (
                        <Polyline positions={positions} pathOptions={{ color, weight: 3, opacity: 0.85 }} />
                      )}
                      {t.pings.map((p, idx) => {
                        const showLabel = idx === 0 || idx === total - 1 || idx % step === 0;
                        return (
                          <CircleMarker
                            key={`${t.supervisor_id}-${idx}`}
                            center={[p.lat, p.long]}
                            radius={4}
                            pathOptions={{ color, fillColor: color, fillOpacity: 0.8, weight: 1 }}
                          >
                            <Tooltip>
                              <div className="text-xs">
                                <div className="font-medium">{t.supervisor_name}</div>
                                <div className="text-muted-foreground">{fmtDateTime(p.captured_at)}</div>
                                <div>±{Math.round(p.accuracy_m || 0)} m</div>
                              </div>
                            </Tooltip>
                            {showLabel ? (
                              <Tooltip
                                permanent
                                direction="right"
                                offset={[6, 0]}
                                className="!bg-white/95 !border !border-slate-200 !shadow-sm !text-[10px] !px-1 !py-0 !rounded"
                              >
                                <span style={{ color }} className="font-medium tabular-nums">
                                  {fmtTime(p.captured_at)}
                                </span>
                              </Tooltip>
                            ) : null}
                          </CircleMarker>
                        );
                      })}
                      {last && (
                        <Marker position={[last.lat, last.long]}>
                          <Tooltip permanent direction="top" offset={[0, -20]}>
                            <span className="text-[11px] font-medium">{t.supervisor_name}</span>
                          </Tooltip>
                        </Marker>
                      )}
                    </React.Fragment>
                  );
                })}
              </MapContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex flex-col gap-2 p-3">
            <div className="flex items-center gap-2 text-sm">
              <MapPin className="h-4 w-4 text-primary" /> Supervisors with pings on {fmtDate(date)}
            </div>
            {trails.length === 0 ? (
              <div className="rounded-md border bg-muted/40 p-4 text-center text-xs text-muted-foreground">
                No GPS pings recorded for this date / filter.
              </div>
            ) : (
              <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                {trails.map((t, i) => {
                  const color = COLORS[i % COLORS.length];
                  const first = t.pings[0];
                  const last = t.pings[t.pings.length - 1];
                  return (
                    <div key={t.supervisor_id} className="flex items-start gap-2 rounded-md border p-2" data-testid={`gps-trail-row-${t.supervisor_id}`}>
                      <span className="mt-1 h-3 w-3 shrink-0 rounded-full" style={{ background: color }} />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{t.supervisor_name}</div>
                        <div className="text-[11px] text-muted-foreground">
                          <Badge variant="secondary" className="mr-1">{t.role === "pmc_supervisor" ? "PMC" : "Contractor"}</Badge>
                          {t.count} pings · {fmtTime(first?.captured_at)} → {fmtTime(last?.captured_at)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
